import React from 'react';
import { Box, Button, Chip, Slider, Stack, Typography } from '@mui/material';
import { BankSearchWeights } from '../../../../utils/types/candi_bank_types';

export interface WeightSlidersProps {
  weights: BankSearchWeights;
  override: Partial<BankSearchWeights> | null;
  onOverrideChange: (override: Partial<BankSearchWeights>) => void;
  onCommit: () => void;
  onReset: () => void;
}

export const WeightSliders: React.FC<WeightSlidersProps> = ({
  weights,
  override,
  onOverrideChange,
  onCommit,
  onReset,
}) => {
  const currentWeights = { ...weights, ...(override || {}) };
  const hasOverride = override !== null && Object.keys(override).length > 0;

  const handleSliderChange = (
    field: keyof BankSearchWeights,
    value: number | number[],
  ) => {
    const numValue = typeof value === 'number' ? value : value[0];
    onOverrideChange({ ...(override || {}), [field]: numValue });
  };

  const renderSlider = (field: keyof BankSearchWeights, label: string) => {
    const val = currentWeights[field] ?? 0;
    const percentage = Math.round(val * 100);

    return (
      <Box
        key={field}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography
          variant='caption'
          fontWeight={600}
          color='text.secondary'
          noWrap
          sx={{ width: 130 }}
        >
          {label}
        </Typography>
        <Slider
          size='small'
          min={0}
          max={1}
          step={0.05}
          value={val}
          onChange={(_, value) => handleSliderChange(field, value)}
          sx={{
            flex: 1,
            color: '#6366f1',
            '& .MuiSlider-thumb': {
              width: 14,
              height: 14,
            },
          }}
        />
        <Typography
          variant='caption'
          fontWeight={700}
          fontFamily='monospace'
          color='text.primary'
          sx={{ width: 36, textAlign: 'right' }}
        >
          {percentage}%
        </Typography>
      </Box>
    );
  };

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 4,
          rowGap: 1.5,
        }}
      >
        {renderSlider('semantic', 'Semantic Similarity')}
        {renderSlider('mandatorySkills', 'Important Skills')}
        {renderSlider('desirableSkills', 'Nice-to-Have Skills')}
        {renderSlider('experience', 'Experience')}
      </Box>

      {hasOverride && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Chip
            size='small'
            label='Unsaved preview'
            sx={{
              bgcolor: '#fef3c7',
              color: '#b45309',
              fontWeight: 600,
              fontSize: '0.6875rem',
              height: 22,
            }}
          />
          <Button
            size='small'
            variant='outlined'
            onClick={onReset}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.75rem',
              borderColor: 'divider',
              color: 'text.secondary',
            }}
          >
            Reset
          </Button>
          <Button
            size='small'
            variant='contained'
            onClick={onCommit}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.75rem',
              bgcolor: '#6366f1',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#4f46e5', boxShadow: 'none' },
            }}
          >
            Save Weights
          </Button>
        </Box>
      )}
    </Stack>
  );
};
